import { Injectable } from '@angular/core';

import { EmailServiceProvider } from '@services/email/email-service/email.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { MailsDataProvider } from '@services/email/mails.data.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { EmailSendData } from '@models/models';

interface MailData {
	chosenEmail: any;
	chosenContract?: any;
}

interface MessageParametres {
	messageData: EmailSendData;
	contract: any;
	jobId: number;
	mailData: MailData;
}

interface DataToSend {
	emailTo: Array<string>;
	ccEmails?: Array<string>;
	secondaryToEmails?: Array<string>;
	emailText: string;
	emailSubject: string;
	contractText?: string;
	contractId: number;
	jobId: number;
	templateId: number;
}

interface ContractMailData {
	sendMessageData: DataToSend;
	storedFiles: Array<any>;
	uploadedFiles: Array<any>;
}

@Injectable()
export class ContractsServiceProvider {
	constructor(
		private emailService: EmailServiceProvider,
		private messageService: MessageService,
		private mailsDataService: MailsDataProvider,
		private stringModifierService: StringModifierServiceProvider
	) {}

	setDefaultEmail(emails: Array<any>) {
		if (emails !== undefined && emails !== null && emails.length) {
			return (
				emails.find(
					item => item.emailTemplateType === 'CONTRACT_EMAIL_TEMPLATE'
				) || {}
			);
		}
		return null;
	}

	errorSendMessage(result): void {
		const { error } = result;
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	successSendEmail() {
		this.messageService.showToaster('s_4', 'jobView');
		this.mailsDataService.emitMailUpdateEvent(true);
	}

	gatherDataToSendContract(
		data: MessageParametres,
		isNew: boolean
	): ContractMailData {
		const { messageData, contract, jobId, mailData } = data;
		const { attachmentsData } = messageData;
		const { storedFiles, uploadedFiles } = this.emailService.prepareAttachments(
			attachmentsData
		);

		let sendMessageData;
		if (isNew) {
			sendMessageData = this.prepareNewContractMailToSend(
				messageData,
				jobId,
				mailData,
				contract
			);
		} else {
			sendMessageData = this.prepareExistingContractMailToSend(
				messageData,
				contract,
				jobId,
				mailData
			);
		}

		return {
			sendMessageData,
			storedFiles,
			uploadedFiles
		};
	}

	prepareContractToSave(
		updatedContract,
		chosenContract,
		currentContractId
	): any {
		let contract;
		if (updatedContract) {
			contract = updatedContract;
			if (currentContractId === updatedContract.id) {
				updatedContract.contractId = currentContractId;
			}
		} else {
			contract = chosenContract;
		}

		if (contract && contract.name) {
			contract.name = this.stringModifierService.cutSelectedText(contract.name);
		}
		return contract;
	}

	checkContractOptIn(contractToSave) {
		/* Fix changed field for GDPR */
		if (contractToSave.optInConsentStatement) {
			contractToSave.optInConsent = contractToSave.optInConsentStatement;
			delete contractToSave.optInConsentStatement;
		}
		/*
          Fix backend grammar mistake
          showOTPinconsent => showOPTinconsent
           */
		if (contractToSave.showOtpInConsent) {
			contractToSave.showOptInConsent = contractToSave.showOtpInConsent;
			delete contractToSave.showOtpInConsen;
		}
		return contractToSave;
	}

	contractTextValid(html: string): boolean {
		const text = new DOMParser().parseFromString(html, 'text/html').body
			.textContent;

		if (!text || text === 'null' || (text && !text.trim())) {
			this.messageService.showErrorFromBackend(
				'Whoops, something has gone wrong!',
				'Contract should have text'
			);
			return false;
		}

		return true;
	}

	private prepareExistingContractMailToSend(
		messageData,
		contract,
		jobId,
		mailData
	): DataToSend {
		const result: DataToSend = {
			emailTo: messageData.emailTo,
			emailText: messageData.emailText || '',
			emailSubject: messageData.emailSubject,
			contractText: contract.text,
			contractId: contract.id,
			jobId,
			templateId: mailData.chosenEmail.id
		};

		if (messageData.secondaryToEmails && messageData.secondaryToEmails.length) {
			result.secondaryToEmails = messageData.secondaryToEmails;
		}

		if (messageData.ccEmails && messageData.ccEmails.length) {
			result.ccEmails = messageData.ccEmails;
		}

		return result;
	}

	private prepareNewContractMailToSend(
		messageData,
		jobId,
		mailData,
		updatedContract
	): DataToSend {
		const contract2Save = updatedContract
			? updatedContract.text
			: mailData.chosenContract.text;

		const result: DataToSend = {
			emailTo: messageData.emailTo,
			emailText: messageData.emailText || '',
			emailSubject: messageData.emailSubject,
			contractId: mailData.chosenContract
				? mailData.chosenContract.templateId
				: null,
			jobId,
			templateId: mailData.chosenEmail.id
		};

		if (contract2Save) {
			result.contractText = contract2Save;
		}

		if (messageData.secondaryToEmails && messageData.secondaryToEmails.length) {
			result.secondaryToEmails = messageData.secondaryToEmails;
		}

		if (messageData.ccEmails && messageData.ccEmails.length) {
			result.ccEmails = messageData.ccEmails;
		}

		return result;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/contract/contracts-service/contracts.service.ts